import { useState, useEffect, useRef } from "react";
import MainView from "views/MainView";
import { FormattedMessage, useIntl } from "react-intl";
import { resetPassword, queryStrToObject } from "request";
import md5 from "md5";
import clsx from "clsx";
import { Spinner } from "components";
import { useNavigate } from "react-router-dom";
import { RouterPath } from "routes/routerPaths";
import { useIntlContext, SupportedLocales } from "views/IntlContext";

// interface ResetData {
//   newPassword: string;
//   code: string;
// }

export const ResetPage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { switchLanguage, locale } = useIntlContext();

  const [error, setError] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const code = useRef<string>("");

  const validatePasswords = () => {
    return (
      password &&
      password.trim() &&
      confirmPassword &&
      confirmPassword.trim() &&
      !error
    );
  };

  const onChangeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    setConfirmPassword(value);
    if (error) {
      setError("");
    }
  };

  const onBlurConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value) value = value.trim();
    if (!value || !password) return;
    if (password !== value) {
      setError(formatMessage({ id: "reset.form.error" }));
    }
  };

  const onBlurPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value) value = value.trim();
    if (!value || !confirmPassword) return;
    if (confirmPassword !== value) {
      setError(formatMessage({ id: "reset.form.error" }));
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || isLoading) return;
    if (confirmPassword !== password) {
      setError(formatMessage({ id: "reset.form.error" }));
      setDisabled(true);
      return;
    }

    try {
      setIsLoading(true);
      const response: any = await resetPassword({
        lang: locale,
        data: { newPassword: md5(password), code: code.current },
      });

      setIsLoading(false);
      const requestStatus = response?.code;
      if (requestStatus === "200" || requestStatus === "411") {
           navigate(
             `/${RouterPath.resetResult}?code=${requestStatus}&lang=${locale}`
           );
        // navigate(`/${RouterPath.resetResult}`, {
        //   state: { code: requestStatus, lang: locale },
        // });
      } else {
        const requestMessage = response?.message || "";
        if (requestMessage) {
          setError(requestMessage);
        }
      }
    } catch (error: any) {
      setIsLoading(false);
      const requestMessage = error?.message || "";
      if (requestMessage) {
        setError(requestMessage);
      }
    }
  };

  useEffect(() => {
    const queryData = queryStrToObject();
    if (queryData) {
      if (queryData?.code) {
        code.current = queryData.code;
      }
      if (queryData?.lang) {
        switchLanguage?.(queryData.lang as SupportedLocales);
      }
    }
  }, []);

  useEffect(() => {
    setDisabled(!validatePasswords());
  }, [password, confirmPassword, error]);

  return (
    <MainView backgroundClassName="bg-white dark:bg-black1b1f1e">
      <div className="pt-11 px-[22px] w-full">
        <h2 className="font-bold text-[28px] mb-[90px]">
          <FormattedMessage id="reset.form.title" />
        </h2>
        <form className="w-full" onSubmit={onSubmit}>
          <div className="w-full">
            <input
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={onBlurPassword}
              placeholder={formatMessage({
                id: "reset.form.password.placeholder",
              })}
              className="border-b border-solid border-[#dcdcdc] dark:border-gray484848 w-full p-2 outline-none text-lg bg-transparent"
            />
          </div>
          <div className="mt-10 relative">
            <input
              type="password"
              name="confirmPassword"
              value={confirmPassword}
              onBlur={onBlurConfirmPassword}
              onChange={onChangeConfirmPassword}
              placeholder={formatMessage({
                id: "reset.form.confirm.password.placeholder",
              })}
              className={clsx(
                "border-b border-solid w-full p-2 outline-none text-lg bg-transparent",
                {
                  "border-[#dcdcdc] dark:border-gray484848": !error,
                  "border-error": error,
                }
              )}
            />
            {error && (
              <p className="text-error mt-2 leading-5 absolute bottom-[-24px] left-0 text-sm">
                {error}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={disabled || isLoading}
            className="w-full h-[54px] bg-primary font-medium rounded-lg border border-sol border-primary text-white text-opacity-90 text-lg flex items-center justify-center mt-[75px] disabled:bg-[#cdcdcd] dark:disabled:bg-gray484848 disabled:border-[#dcdcdc] dark:disabled:border-gray484848"
          >
            {isLoading ? (
              <Spinner
                extraSmall
                className={{ "border-gray666 dark:border-white60": true }}
              />
            ) : (
              <FormattedMessage id="reset.form.button" />
            )}
          </button>
        </form>
      </div>
    </MainView>
  );
};
